import {
  Badge,
  Button,
  cn,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@beweegbaas/ui";
import { useAtomSet } from "@effect/atom-react";
import { useQuery } from "@tanstack/react-query";
import { useId, useState, type ReactNode } from "react";
import { RouteDialog } from "#/components/route-dialog";
import { VakantiePeriode, VrijeDagRij } from "#/modules/les.planning/components/vakanties-form";
import { getSchooljaarRange, type SchooljaarRange } from "#/modules/les.planning/domain/calendar";
import type { Roosterregel } from "#/modules/les.planning/domain/rooster";
import {
  EMPTY_VAKANTIES,
  formatDays,
  getSchoolFeestdagen,
  VAKANTIEREGIOS,
  type Vakantieregio,
  type VakantiesOverzicht,
} from "#/modules/les.planning/domain/vakanties";
import { roosterQueryOptions, vakantiesQueryOptions } from "#/modules/les.planning/query.planning";
import {
  changeVakantieregioAtom,
  deleteVrijeDagAtom,
} from "#/modules/les.planning/vakanties/vakanties-editor";
import type { TenantQueryKey } from "#/modules/util.fetch-client/tenant-query-key";

const NO_REGELS: readonly Roosterregel[] = [];

/**
 * The days the whole school has no gym, as a route dialog: the schoolvakanties and feestdagen of
 * its regio and its own vrije dagen. `onClose` goes back to the page it opened over.
 */
export function VakantiesDialog({
  tenant,
  schoolName,
  onClose,
}: {
  tenant: TenantQueryKey;
  schoolName: string;
  onClose: () => void;
}) {
  // A new vrije dag being typed in, as a row of its own at the end of the list.
  const [nieuw, setNieuw] = useState(false);
  const overzicht = useQuery(vakantiesQueryOptions(tenant)).data ?? EMPTY_VAKANTIES;
  const regels = useQuery(roosterQueryOptions(tenant)).data?.regels ?? NO_REGELS;
  const range = getSchooljaarRange(tenant.schoolyear);

  return (
    <RouteDialog
      onClose={onClose}
      onOpenChange={(next, details) => {
        // Escape takes back a new vrije dag first; the dialog stays.
        if (!next && details.reason === "escape-key" && nieuw) {
          setNieuw(false);
          details.cancel();
        }
      }}
    >
      <DialogContent className="md:max-w-150" data-testid="vakanties-dialog">
        <DialogHeader className="md:pr-8">
          <DialogTitle>{m["planning.vakanties.titel"]()}</DialogTitle>
          <DialogDescription>{m["planning.vakanties.uitleg"]()}</DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-5">
          <Schoolvakanties overzicht={overzicht} schoolName={schoolName} range={range} />
          <EigenVrijeDagen
            overzicht={overzicht}
            regels={regels}
            range={range}
            nieuw={nieuw}
            onNieuwChange={setNieuw}
          />
        </DialogBody>
        <p className="text-muted-foreground m-0 border-t pt-3 text-xs md:-mx-6 md:-mb-6 md:px-6 md:pb-3">
          {m["planning.vakanties.direct_opgeslagen"]()}
        </p>
      </DialogContent>
    </RouteDialog>
  );
}

function Schoolvakanties({
  overzicht,
  schoolName,
  range,
}: {
  overzicht: VakantiesOverzicht;
  schoolName: string;
  range: SchooljaarRange;
}) {
  const titleId = useId();
  const changeRegio = useAtomSet(changeVakantieregioAtom);
  const { vakantieregio, vakanties } = overzicht;
  const feestdagen = getSchoolFeestdagen(overzicht.feestdagen, vakanties);
  const regioSelect = <RegioSelect value={vakantieregio} onChange={changeRegio} />;

  return (
    <section aria-labelledby={titleId} className="flex flex-col">
      <SectionHeader
        id={titleId}
        title={m["planning.vakanties.schoolvakanties"]()}
        sub={m["planning.vakanties.schoolvakanties_bron"]()}
        action={vakantieregio ? regioSelect : null}
      />
      {vakantieregio ? (
        <ul className="m-0 flex list-none flex-col p-0">
          {vakanties.map((vakantie) => (
            <li key={vakantie.naam} className={rowClassName}>
              <span className={cn(naamClassName, "flex flex-col")}>
                <span className="truncate font-medium">{vakantie.naam}</span>
                {vakantie.aangepast ? (
                  // A phone has no room for the badge next to the dates.
                  <span className="text-muted-foreground text-xs sm:hidden">
                    {m["planning.vakanties.aangepast"]()}
                  </span>
                ) : null}
              </span>
              <VakantiePeriode vakantie={vakantie} vakantieregio={vakantieregio} range={range} />
              {vakantie.aangepast ? (
                <Badge variant="muted" size="sm" className="shrink-0 font-semibold max-sm:hidden">
                  {m["planning.vakanties.aangepast"]()}
                </Badge>
              ) : null}
            </li>
          ))}
          {feestdagen.length > 0 ? (
            <li className={rowClassName}>
              <span className={cn(naamClassName, "font-medium")}>
                {m["planning.vakanties.feestdagen"]()}
              </span>
              <span className="text-muted-foreground min-w-0 tabular-nums">
                {formatDays(feestdagen)}
              </span>
            </li>
          ) : null}
        </ul>
      ) : (
        <div className="flex flex-col items-start gap-3 pt-1">
          <p className="text-muted-foreground m-0 text-sm">
            {m["planning.vakanties.regio_vraag"]({ school: schoolName })}
          </p>
          {regioSelect}
        </div>
      )}
    </section>
  );
}

function EigenVrijeDagen({
  overzicht,
  regels,
  range,
  nieuw,
  onNieuwChange,
}: {
  overzicht: VakantiesOverzicht;
  regels: readonly Roosterregel[];
  range: SchooljaarRange;
  nieuw: boolean;
  onNieuwChange: (nieuw: boolean) => void;
}) {
  const titleId = useId();
  const remove = useAtomSet(deleteVrijeDagAtom);
  const rows = overzicht.vrijeDagen;

  return (
    <section aria-labelledby={titleId} className="flex flex-col">
      <SectionHeader
        id={titleId}
        title={m["planning.vakanties.eigen"]()}
        sub={m["planning.vakanties.eigen_uitleg"]()}
      />
      {rows.length === 0 && !nieuw ? (
        <p className="text-muted-foreground m-0 py-1 text-sm">
          {m["planning.vakanties.geen_eigen"]()}
        </p>
      ) : null}
      {rows.length > 0 ? (
        <ul className="m-0 flex list-none flex-col p-0">
          {rows.map((vrijeDag) => (
            <li key={vrijeDag.id}>
              <VrijeDagRij
                // A change from elsewhere, such as an undo, starts the row over.
                key={`${vrijeDag.naam}|${vrijeDag.start}|${vrijeDag.eind}`}
                vrijeDag={vrijeDag}
                overzicht={overzicht}
                regels={regels}
                range={range}
                onDelete={() => remove(vrijeDag)}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {nieuw ? (
        <VrijeDagRij
          overzicht={overzicht}
          regels={regels}
          range={range}
          onDelete={() => onNieuwChange(false)}
          onDone={() => onNieuwChange(false)}
        />
      ) : (
        <Button
          type="button"
          variant="link"
          className="mt-2 h-auto self-start px-0 font-semibold"
          onClick={() => onNieuwChange(true)}
        >
          <IconMaterialSymbolsAdd />
          {m["planning.vakanties.vrije_dag"]()}
        </Button>
      )}
    </section>
  );
}

function SectionHeader({
  id,
  title,
  sub,
  action,
}: {
  id: string;
  title: string;
  sub: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex min-h-9 items-center gap-3 pb-1">
      <h3
        id={id}
        className="m-0 flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 font-sans text-sm font-semibold"
      >
        {title}
        <span className="text-muted-foreground text-xs font-normal">{sub}</span>
      </h3>
      {action}
    </div>
  );
}

function RegioSelect({
  value,
  onChange,
}: {
  value: Vakantieregio | null;
  onChange: (vakantieregio: Vakantieregio) => void;
}) {
  return (
    <Select<Vakantieregio>
      value={value}
      onValueChange={(next) => {
        if (next) onChange(next);
      }}
    >
      <SelectTrigger
        aria-label={m["planning.vakanties.vakantieregio"]()}
        className={cn("w-auto shrink-0", value === null && "min-w-56")}
      >
        <SelectValue>
          {value
            ? m["planning.vakanties.regio"]({ regio: value })
            : m["planning.vakanties.regio_kiezen"]()}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {VAKANTIEREGIOS.map((regio) => (
          <SelectItem key={regio} value={regio}>
            {m["planning.vakanties.regio"]({ regio })}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const rowClassName = "flex h-10 items-center gap-3 text-sm sm:h-11";
// Narrower on a phone, so the dates fit beside it.
const naamClassName = "w-34 shrink-0 sm:w-45";
